import React, { useState, useEffect } from 'react';
import { Sparkles, Check, TrendingUp, Users, MapPin, Star, Award, Loader2, ArrowRight } from 'lucide-react';
import { publicApi } from '../api';
import { useFetch } from '../useFetch';

const TIERS = [
  {
    key: 'featured', label: 'In evidenza', price: 'da €29/mese',
    icon: TrendingUp,
    perks: [
      'Card evidenziata con badge oro',
      'Posizionamento in cima ai risultati di categoria',
      'Rating e stelle in vista',
      'CTA Chiama / Sito / Indicazioni sempre attivi'
    ]
  },
  {
    key: 'top3', label: 'Top 3', price: 'da €79/mese',
    icon: Award,
    perks: [
      'Badge numerato 1°/2°/3° posto',
      'Pin oro grande sulla mappa',
      'Inclusione automatica nel sort "In evidenza"',
      'Priorità nelle categorie più cercate'
    ]
  },
  {
    key: 'recommended', label: 'Consigliato da YUP', price: 'da €149/mese', highlighted: true,
    icon: Star,
    perks: [
      'Nastro oro "Consigliato da YUP"',
      'Prima posizione assoluta',
      'Menzione negli itinerari editoriali AI',
      'Statistiche mensili complete + report CSV',
      'Priorità su banner e chat AI'
    ]
  },
  {
    key: 'custom', label: 'Su misura', price: 'contattaci',
    icon: Sparkles,
    perks: [
      'Progetti personalizzati',
      'YUP Experience dedicata',
      'Video reel + shooting fotografico',
      'Campagne multi-città'
    ]
  }
];

const EMPTY = { business_name: '', contact_name: '', email: '', phone: '', website: '', city_slug: '', category: '', tier: 'featured', message: '' };

export default function PromoteBusiness() {
  const [f, setF] = useState({...EMPTY});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const { data: cities } = useFetch('cities', publicApi.cities);
  const { data: cats } = useFetch('cats', publicApi.categories);

  useEffect(() => {
    // Deep-link support: ?tier=top3
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tier');
    if (t && TIERS.find(x => x.key === t)) setF(v => ({ ...v, tier: t }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.business_name || !f.contact_name || !f.email) { setErr('Nome attività, contatto ed email sono obbligatori'); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      await publicApi.submitPartnerLead(payload);
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail?.[0]?.msg || e2.response?.data?.detail || 'Errore invio, riprova');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="promote-page">
      {/* HERO */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full min-h-[55vh] md:min-h-[65vh] flex items-center">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&auto=format&fit=crop&q=80"
            alt="Promuovi la tua attività"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 text-white text-center w-full">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-black mb-6"
                  style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
              <Sparkles size={12}/> YUP Partner Program
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Promuovi la tua attività<br/>
              <span className="text-[color:var(--yup-primary)] italic">sul Garda che conta</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-2xl mx-auto">
              YUP porta ogni giorno migliaia di turisti a scoprire il Lago di Garda. Fatti trovare per primo, con badge esclusivi, mappa premium e statistiche in tempo reale.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><Users size={16} className="text-[color:var(--yup-primary)]"/> +50k visite/mese</span>
              <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-[color:var(--yup-primary)]"/> 3 città attive</span>
              <span className="inline-flex items-center gap-2"><TrendingUp size={16} className="text-[color:var(--yup-primary)]"/> ROI misurabile</span>
            </div>
            <a href="#form" className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-black text-sm tracking-wider uppercase shadow-2xl hover:scale-105 transition-transform"
               style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
              Candida la tua attività <ArrowRight size={16}/>
            </a>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-xs tracking-[0.3em] uppercase text-[color:var(--yup-primary)] font-semibold mb-2">Scegli il tuo piano</h2>
          <p className="text-white text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Piani flessibili, risultati concreti</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t, i) => {
            const Icon = t.icon;
            const selected = f.tier === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setF({...f, tier: t.key}); document.getElementById('form')?.scrollIntoView({behavior:'smooth'}); }}
                className={`relative text-left rounded-2xl p-6 border transition-all fade-in-up ${
                  selected
                    ? 'border-[color:var(--yup-primary)] bg-gradient-to-br from-[color:var(--yup-primary)]/15 to-black/60 shadow-2xl'
                    : t.highlighted
                      ? 'border-[color:var(--yup-primary)]/60 bg-black/50 hover:border-[color:var(--yup-primary)]'
                      : 'border-white/10 bg-black/40 hover:border-white/30'
                }`}
                style={{ animationDelay: `${i * 0.06}s` }}
                data-testid={`tier-${t.key}`}
              >
                {t.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-black"
                        style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>Popolare</span>
                )}
                <Icon size={28} className="text-[color:var(--yup-primary)] mb-4"/>
                <h3 className="text-white font-bold text-xl">{t.label}</h3>
                <p className="text-[color:var(--yup-primary)] text-sm mt-1 font-semibold">{t.price}</p>
                <ul className="mt-5 space-y-2">
                  {t.perks.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-white/75 text-sm"><Check size={14} className="text-[color:var(--yup-primary)] mt-0.5 flex-shrink-0"/> <span>{p}</span></li>
                  ))}
                </ul>
                <div className={`mt-6 text-xs uppercase tracking-widest font-semibold ${selected ? 'text-[color:var(--yup-primary)]' : 'text-white/50'}`}>
                  {selected ? '✓ Selezionato' : 'Seleziona questo piano →'}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="max-w-3xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-black/60 backdrop-blur-md border border-[color:var(--yup-primary)]/40 p-8 md:p-10 shadow-2xl">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Candida la tua attività</h2>
          <p className="text-white/60 text-sm mb-8">Compila il form: lo staff YUP ti ricontatta entro 24-48h con una proposta personalizzata.</p>

          {sent ? (
            <div className="text-center py-10" data-testid="promote-success">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                   style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
                <Check size={32} className="text-black"/>
              </div>
              <h3 className="text-white text-xl font-bold">Grazie! Richiesta ricevuta.</h3>
              <p className="text-white/70 mt-2">Ti ricontattiamo presto all'email <strong className="text-[color:var(--yup-primary)]">{f.email}</strong>.</p>
              <button onClick={() => { setSent(false); setF({...EMPTY}); }} className="mt-6 text-[color:var(--yup-primary)] text-sm underline">Invia un'altra candidatura</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="promote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Nome attività *" value={f.business_name} onChange={v => setF({...f, business_name: v})} testid="lead-business" required/>
                <FormInput label="Referente *" value={f.contact_name} onChange={v => setF({...f, contact_name: v})} testid="lead-contact" required/>
                <FormInput label="Email *" type="email" value={f.email} onChange={v => setF({...f, email: v})} testid="lead-email" required/>
                <FormInput label="Telefono" value={f.phone} onChange={v => setF({...f, phone: v})} placeholder="+39 ..." testid="lead-phone"/>
                <FormInput label="Sito web" value={f.website} onChange={v => setF({...f, website: v})} placeholder="https://..." testid="lead-website"/>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">Città</label>
                  <select value={f.city_slug} onChange={e => setF({...f, city_slug: e.target.value})}
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--yup-primary)]"
                          data-testid="lead-city">
                    <option value="">Seleziona città...</option>
                    {(cities || []).map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">Categoria</label>
                  <select value={f.category} onChange={e => setF({...f, category: e.target.value})}
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--yup-primary)]"
                          data-testid="lead-category">
                    <option value="">Seleziona categoria...</option>
                    {(cats || []).map(c => <option key={c.key} value={c.key}>{c.label?.it || c.key}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">Piano di interesse</label>
                  <select value={f.tier} onChange={e => setF({...f, tier: e.target.value})}
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--yup-primary)]"
                          data-testid="lead-tier">
                    {TIERS.map(t => <option key={t.key} value={t.key}>{t.label} — {t.price}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">Raccontaci</label>
                <textarea value={f.message} onChange={e => setF({...f, message: e.target.value})}
                          rows={4} placeholder="Da quanto sei sul mercato, cosa ti rende speciale, obiettivi..."
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[color:var(--yup-primary)]"
                          data-testid="lead-message"/>
              </div>

              {err && <div className="text-red-400 text-sm" data-testid="promote-error">{err}</div>}

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                <button type="submit" disabled={busy}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-black text-sm tracking-wider uppercase shadow-xl hover:scale-105 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
                        data-testid="lead-submit-btn">
                  {busy ? <><Loader2 size={16} className="animate-spin"/> Invio...</> : <>Invia candidatura <ArrowRight size={16}/></>}
                </button>
                <p className="text-white/40 text-xs">Zero impegno. Nessun addebito automatico.</p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function FormInput({ label, value, onChange, type = 'text', placeholder, required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">{label}</label>
      <input type={type} value={value} required={required} placeholder={placeholder}
             onChange={e => onChange(e.target.value)}
             data-testid={testid}
             className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[color:var(--yup-primary)]"/>
    </div>
  );
}
